import React, { useEffect, useState, useCallback, useRef, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../contexts/AuthContext";
import {
    getAdminStats,
    getAdminUsers,
    getAdminUserDetail,
    resyncUserTransactions,
    toggleFreeAccess,
    getSystemHealth,
} from "../../api/adminClient";

function formatDate(dt) {
    if (!dt) return "-";
    return new Date(dt).toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
    });
}

function formatDateTime(dt) {
    if (!dt) return "-";
    return new Date(dt).toLocaleString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
        hour: "numeric",
        minute: "2-digit",
    });
}

function timeAgo(dt) {
    if (!dt) return "never";
    const diff = Date.now() - new Date(dt).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return "just now";
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    return `${days}d ago`;
}

const STATUS_COLORS = {
    active: "bg-accent-50 text-accent-600",
    trialing: "bg-accent-50 text-accent-600",
    canceled: "bg-red-50 text-danger-500",
    past_due: "bg-amber-50 text-amber-600",
    inactive: "bg-gray-100 text-gray-500",
    none: "bg-gray-100 text-gray-500",
    free_access: "bg-indigo-50 text-indigo-600",
};

function StatusBadge({ status }) {
    const color = STATUS_COLORS[status] || STATUS_COLORS.none;
    const label = status === "free_access" ? "Free" : status || "none";
    return (
        <span className={`text-xs px-2 py-0.5 rounded-full ${color}`}>
            {label}
        </span>
    );
}

// ==================== Stat Card ====================

function StatCard({ label, value, sub }) {
    return (
        <div className="card-solid p-4">
            <p className="text-xs text-gray-500 mb-1">{label}</p>
            <p className="text-xl font-bold text-gray-900">{value}</p>
            {sub && <p className="text-xs text-gray-500 mt-1">{sub}</p>}
        </div>
    );
}

// ==================== Overview Section ====================

function OverviewSection({ stats }) {
    if (!stats) return null;
    return (
        <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">Overview</h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                <StatCard label="Total Users" value={stats.totalUsers} />
                <StatCard label="Paying Subscribers" value={stats.payingSubscribers} />
                <StatCard label="Free Trial" value={stats.trialing} />
                <StatCard label="Canceled" value={stats.canceled} />
                <StatCard label="Free Access" value={stats.freeAccess} />
                <StatCard
                    label="MRR (Net)"
                    value={`$${Number(stats.mrr).toFixed(2)}`}
                />
                <StatCard
                    label="MRR (Gross)"
                    value={`$${Number(stats.mrrGross).toFixed(2)}`}
                />
                <StatCard label="Signups (7d)" value={stats.signups7d} />
                <StatCard label="Signups (30d)" value={stats.signups30d} />
            </div>
        </div>
    );
}

// ==================== User Table ====================

function UserTable({ users, onSelectUser }) {
    const [search, setSearch] = useState("");
    const [statusFilter, setStatusFilter] = useState("ALL");
    const [sortKey, setSortKey] = useState("createdAt");
    const [sortDesc, setSortDesc] = useState(true);

    const handleSort = (key) => {
        if (sortKey === key) {
            setSortDesc(!sortDesc);
        } else {
            setSortKey(key);
            setSortDesc(true);
        }
    };

    const filtered = useMemo(() => {
        let result = users || [];
        if (search) {
            const q = search.toLowerCase();
            result = result.filter(
                (u) =>
                    u.email?.toLowerCase().includes(q) ||
                    u.displayName?.toLowerCase().includes(q)
            );
        }
        if (statusFilter !== "ALL") {
            result = result.filter((u) => u.subscriptionStatus === statusFilter);
        }
        result = [...result].sort((a, b) => {
            let aVal = a[sortKey];
            let bVal = b[sortKey];
            if (aVal == null) return 1;
            if (bVal == null) return -1;
            if (typeof aVal === "string") aVal = aVal.toLowerCase();
            if (typeof bVal === "string") bVal = bVal.toLowerCase();
            if (aVal < bVal) return sortDesc ? 1 : -1;
            if (aVal > bVal) return sortDesc ? -1 : 1;
            return 0;
        });
        return result;
    }, [users, search, statusFilter, sortKey, sortDesc]);

    const SortHeader = ({ label, field, className = "" }) => (
        <th
            className={`text-left text-xs font-medium text-gray-500 px-3 py-2 cursor-pointer hover:text-gray-900 select-none ${className}`}
            onClick={() => handleSort(field)}
        >
            {label}
            {sortKey === field && (
                <span className="ml-1">{sortDesc ? "▼" : "▲"}</span>
            )}
        </th>
    );

    return (
        <div>
            <div className="flex items-center justify-between mb-3">
                <h2 className="text-lg font-semibold text-gray-900">Users</h2>
                <div className="flex gap-2">
                    <input
                        type="text"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        placeholder="Search email or name..."
                        className="bg-white border border-gray-200 rounded px-3 py-1.5 text-sm text-gray-900 placeholder-gray-400 focus:border-accent-500 focus:outline-none w-56"
                    />
                    <select
                        value={statusFilter}
                        onChange={(e) => setStatusFilter(e.target.value)}
                        className="bg-white border border-gray-200 rounded px-2 py-1.5 text-sm text-gray-700 focus:outline-none"
                    >
                        <option value="ALL">All statuses</option>
                        <option value="active">Active</option>
                        <option value="trialing">Trialing</option>
                        <option value="canceled">Canceled</option>
                        <option value="inactive">Inactive</option>
                        <option value="free_access">Free Access</option>
                    </select>
                </div>
            </div>

            <div className="card-solid overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="border-b border-gray-200">
                            <tr>
                                <SortHeader label="Email" field="email" />
                                <SortHeader label="Name" field="displayName" />
                                <SortHeader label="Signed Up" field="createdAt" />
                                <SortHeader label="Status" field="subscriptionStatus" />
                                <SortHeader label="Last Login" field="lastLoginAt" />
                                <SortHeader label="Accounts" field="linkedAccounts" className="text-center" />
                                <th className="text-left text-xs font-medium text-gray-500 px-3 py-2">Role</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filtered.map((u) => (
                                <tr
                                    key={u.id}
                                    onClick={() => onSelectUser(u.id)}
                                    className="border-b border-gray-200 hover:bg-gray-50 cursor-pointer transition-colors"
                                >
                                    <td className="px-3 py-2.5 text-gray-900">{u.email}</td>
                                    <td className="px-3 py-2.5 text-gray-700">
                                        {u.displayName || "-"}
                                    </td>
                                    <td className="px-3 py-2.5 text-gray-500">
                                        {formatDate(u.createdAt)}
                                    </td>
                                    <td className="px-3 py-2.5">
                                        <StatusBadge status={u.subscriptionStatus} />
                                    </td>
                                    <td className="px-3 py-2.5 text-gray-500">
                                        {timeAgo(u.lastLoginAt)}
                                    </td>
                                    <td className="px-3 py-2.5 text-gray-500 text-center">
                                        {u.linkedAccounts}
                                    </td>
                                    <td className="px-3 py-2.5 text-gray-500 text-xs">
                                        {u.roles?.includes("ADMIN") ? "Admin" : "User"}
                                    </td>
                                </tr>
                            ))}
                            {filtered.length === 0 && (
                                <tr>
                                    <td colSpan={7} className="px-3 py-8 text-center text-gray-500">
                                        No users found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
                <div className="px-3 py-2 border-t border-gray-200 text-xs text-gray-500">
                    {filtered.length} of {users?.length || 0} users
                </div>
            </div>
        </div>
    );
}

// ==================== User Detail Panel ====================

function UserDetailPanel({ userId, onClose, onRefreshUsers }) {
    const [detail, setDetail] = useState(null);
    const [loading, setLoading] = useState(true);
    const [resyncing, setResyncing] = useState(false);
    const [togglingFree, setTogglingFree] = useState(false);
    const abortRef = useRef(null);

    useEffect(() => {
        if (!userId) return;
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();

        setLoading(true);
        getAdminUserDetail(userId, abortRef.current.signal)
            .then(setDetail)
            .catch((e) => {
                if (e.name !== "AbortError" && e.name !== "CanceledError") {
                    console.error("Failed to load user detail", e);
                }
            })
            .finally(() => setLoading(false));

        return () => abortRef.current?.abort();
    }, [userId]);

    const handleResync = async () => {
        setResyncing(true);
        try {
            await resyncUserTransactions(userId);
        } catch (e) {
            console.error("Resync failed", e);
        } finally {
            setResyncing(false);
        }
    };

    const handleToggleFree = async () => {
        if (!detail) return;
        setTogglingFree(true);
        try {
            await toggleFreeAccess(userId, !detail.freeAccess);
            setDetail((prev) => ({ ...prev, freeAccess: !prev.freeAccess }));
            onRefreshUsers?.();
        } catch (e) {
            console.error("Toggle free access failed", e);
        } finally {
            setTogglingFree(false);
        }
    };

    if (!userId) return null;

    return (
        <div className="card-solid p-5">
            <div className="flex items-center justify-between mb-4">
                <h3 className="text-base font-semibold text-gray-900">User Detail</h3>
                <button
                    onClick={onClose}
                    className="text-gray-500 hover:text-gray-900 text-lg leading-none"
                >
                    &times;
                </button>
            </div>

            {loading ? (
                <div className="space-y-3">
                    {[...Array(4)].map((_, i) => (
                        <div key={i} className="h-4 bg-gray-100 rounded animate-pulse" />
                    ))}
                </div>
            ) : !detail ? (
                <p className="text-gray-500 text-sm">Failed to load user</p>
            ) : (
                <div className="space-y-4">
                    {/* Basic Info */}
                    <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                            <p className="text-gray-500 text-xs">Email</p>
                            <p className="text-gray-900">{detail.email}</p>
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs">Display Name</p>
                            <p className="text-gray-900">{detail.displayName || "-"}</p>
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs">Signed Up</p>
                            <p className="text-gray-700">{formatDateTime(detail.createdAt)}</p>
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs">Last Login</p>
                            <p className="text-gray-700">{formatDateTime(detail.lastLoginAt)}</p>
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs">Subscription</p>
                            <StatusBadge status={detail.subscriptionStatus} />
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs">Period Ends</p>
                            <p className="text-gray-700">
                                {formatDate(detail.subscriptionPeriodEnd)}
                            </p>
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs">Stripe Customer</p>
                            <p className="text-gray-500 text-xs font-mono truncate">
                                {detail.stripeCustomerId || "-"}
                            </p>
                        </div>
                        <div>
                            <p className="text-gray-500 text-xs">Transactions</p>
                            <p className="text-gray-900">{detail.transactionCount?.toLocaleString()}</p>
                        </div>
                    </div>

                    {/* Linked Accounts */}
                    <div>
                        <p className="text-xs text-gray-500 mb-2">
                            Linked Accounts ({detail.linkedAccounts?.length || 0})
                        </p>
                        {detail.linkedAccounts?.length > 0 ? (
                            <div className="space-y-1">
                                {detail.linkedAccounts.map((link) => (
                                    <div
                                        key={link.id}
                                        className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm"
                                    >
                                        <div>
                                            <span className="text-gray-900">
                                                {link.institutionDisplayName || "Unknown"}
                                            </span>
                                            <span
                                                className={`ml-2 text-xs ${
                                                    link.connectionStatus === "HEALTHY"
                                                        ? "text-accent-600"
                                                        : link.needsAttention
                                                        ? "text-danger-500"
                                                        : "text-gray-500"
                                                }`}
                                            >
                                                {link.connectionStatus}
                                            </span>
                                        </div>
                                        <span className="text-xs text-gray-500">
                                            synced {timeAgo(link.lastSuccessfulSyncAt)}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <p className="text-gray-400 text-sm">No accounts linked</p>
                        )}
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2 pt-2 border-t border-gray-200">
                        <button
                            onClick={handleResync}
                            disabled={resyncing}
                            className="px-3 py-1.5 bg-gray-200 hover:bg-gray-300 disabled:opacity-50 text-gray-900 text-sm rounded transition-colors"
                        >
                            {resyncing ? "Resyncing..." : "Resync Transactions"}
                        </button>
                        <button
                            onClick={handleToggleFree}
                            disabled={togglingFree}
                            className={`px-3 py-1.5 text-sm rounded transition-colors disabled:opacity-50 ${
                                detail.freeAccess
                                    ? "bg-red-50 text-danger-500 hover:bg-red-100"
                                    : "bg-indigo-50 text-indigo-600 hover:bg-indigo-100"
                            }`}
                        >
                            {togglingFree
                                ? "..."
                                : detail.freeAccess
                                ? "Revoke Free Access"
                                : "Grant Free Access"}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
}

// ==================== System Health ====================

function SystemHealthSection({ health }) {
    if (!health) return null;

    return (
        <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-3">System Health</h2>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4">
                <StatCard
                    label="Database"
                    value={health.plaidDbAccess ? "OK" : "Error"}
                />
                <StatCard label="Total Connections" value={health.totalConnections} />
                <StatCard label="Healthy" value={health.healthyConnections} />
                <StatCard
                    label="Needs Attention"
                    value={health.connectionsNeedingAttention}
                    sub={
                        health.connectionsNeedingAttention > 0
                            ? "Check connection issues below"
                            : undefined
                    }
                />
            </div>

            {/* Connection Issues */}
            {health.connectionIssues?.length > 0 && (
                <div className="card-solid p-4 mb-4">
                    <p className="text-sm font-medium text-danger-500 mb-2">
                        Connection Issues ({health.connectionIssues.length})
                    </p>
                    <div className="space-y-2">
                        {health.connectionIssues.map((issue, i) => (
                            <div
                                key={i}
                                className="flex items-center justify-between bg-gray-50 border border-gray-200 rounded px-3 py-2 text-sm"
                            >
                                <div>
                                    <span className="text-gray-900">{issue.userEmail}</span>
                                    <span className="text-gray-500 mx-2">→</span>
                                    <span className="text-gray-700">{issue.institution}</span>
                                    <span className="text-danger-500 text-xs ml-2">
                                        {issue.status}
                                    </span>
                                </div>
                                <div className="text-xs text-gray-500">
                                    {issue.consecutiveFailures} failures
                                    {issue.lastErrorAt && ` · ${timeAgo(issue.lastErrorAt)}`}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* Sync Status */}
            {health.syncStatus?.length > 0 && (
                <div className="card-solid p-4">
                    <p className="text-sm font-medium text-gray-700 mb-2">Last Sync by User</p>
                    <div className="space-y-1 max-h-48 overflow-y-auto">
                        {health.syncStatus.map((s, i) => (
                            <div
                                key={i}
                                className="flex items-center justify-between text-sm px-2 py-1"
                            >
                                <span className="text-gray-700">{s.email}</span>
                                <span className="text-gray-500 text-xs">
                                    {timeAgo(s.lastSync)}
                                </span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}

// ==================== Main Admin Page ====================

export default function AdminPage() {
    const { user: authUser } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState(null);
    const [users, setUsers] = useState([]);
    const [health, setHealth] = useState(null);
    const [selectedUserId, setSelectedUserId] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Redirect non-admins immediately
    useEffect(() => {
        if (authUser && !authUser.roles?.includes("ROLE_ADMIN")) {
            navigate("/portfolio", { replace: true });
        }
    }, [authUser, navigate]);
    const abortRef = useRef(null);

    const fetchAll = useCallback(async () => {
        if (abortRef.current) abortRef.current.abort();
        abortRef.current = new AbortController();
        const signal = abortRef.current.signal;

        setLoading(true);
        setError(null);
        try {
            const [statsResp, usersResp, healthResp] = await Promise.all([
                getAdminStats(signal),
                getAdminUsers(signal),
                getSystemHealth(signal),
            ]);
            setStats(statsResp);
            setUsers(usersResp);
            setHealth(healthResp);
        } catch (e) {
            if (e.name === "AbortError" || e.name === "CanceledError") return;
            if (e.response?.status === 403) {
                setError("Access denied. Admin role required.");
            } else {
                setError("Failed to load admin data.");
            }
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchAll();
        return () => abortRef.current?.abort();
    }, [fetchAll]);

    const refreshUsers = useCallback(async () => {
        try {
            const signal = new AbortController().signal;
            const [usersResp, statsResp] = await Promise.all([
                getAdminUsers(signal),
                getAdminStats(signal),
            ]);
            setUsers(usersResp);
            setStats(statsResp);
        } catch {
            // silent
        }
    }, []);

    if (loading) {
        return (
            <div className="max-w-7xl mx-auto">
                <h1 className="text-xl font-bold text-gray-900 mb-6">Admin Dashboard</h1>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    {[...Array(8)].map((_, i) => (
                        <div key={i} className="h-20 bg-white border border-gray-200 rounded-xl animate-pulse" />
                    ))}
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="max-w-7xl mx-auto">
                <h1 className="text-xl font-bold text-gray-900 mb-6">Admin Dashboard</h1>
                <div className="card-solid p-8 text-center">
                    <p className="text-danger-500 mb-2">{error}</p>
                    <button onClick={fetchAll} className="text-sm text-gray-500 hover:text-gray-900">
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="max-w-7xl mx-auto space-y-8">
            <div className="flex items-center justify-between">
                <h1 className="text-xl font-bold text-gray-900">Admin Dashboard</h1>
                <button
                    onClick={fetchAll}
                    className="text-xs text-gray-500 hover:text-gray-900 transition-colors"
                >
                    Refresh
                </button>
            </div>

            <OverviewSection stats={stats} />

            <div className={selectedUserId ? "grid grid-cols-1 lg:grid-cols-3 gap-6" : ""}>
                <div className={selectedUserId ? "lg:col-span-2" : ""}>
                    <UserTable users={users} onSelectUser={setSelectedUserId} />
                </div>
                {selectedUserId && (
                    <div>
                        <UserDetailPanel
                            userId={selectedUserId}
                            onClose={() => setSelectedUserId(null)}
                            onRefreshUsers={refreshUsers}
                        />
                    </div>
                )}
            </div>

            <SystemHealthSection health={health} />
        </div>
    );
}
